import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from '../../../core/services/auth.service';

function passwordsMatch(control: AbstractControl) {
  const pw = control.get('newPassword')?.value;
  const confirm = control.get('confirmPassword')?.value;
  return pw && confirm && pw !== confirm ? { mismatch: true } : null;
}

@Component({
  selector: 'app-change-password-dialog',
  standalone: false,
  template: `
    <h2 mat-dialog-title>Change Password</h2>
    <mat-dialog-content>
      <form [formGroup]="form" style="display:flex;flex-direction:column;gap:4px;min-width:320px;padding-top:8px">
        <mat-form-field appearance="outline">
          <mat-label>Current Password</mat-label>
          <input matInput [type]="hide.current ? 'password' : 'text'" formControlName="currentPassword" autocomplete="current-password">
          <button mat-icon-button matSuffix type="button" (click)="hide.current=!hide.current">
            <mat-icon>{{hide.current ? 'visibility_off' : 'visibility'}}</mat-icon>
          </button>
          <mat-error *ngIf="form.get('currentPassword')?.hasError('required')">Required</mat-error>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>New Password</mat-label>
          <input matInput [type]="hide.new ? 'password' : 'text'" formControlName="newPassword" autocomplete="new-password">
          <button mat-icon-button matSuffix type="button" (click)="hide.new=!hide.new">
            <mat-icon>{{hide.new ? 'visibility_off' : 'visibility'}}</mat-icon>
          </button>
          <mat-error *ngIf="form.get('newPassword')?.hasError('required')">Required</mat-error>
          <mat-error *ngIf="form.get('newPassword')?.hasError('minlength')">Minimum 6 characters</mat-error>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Confirm New Password</mat-label>
          <input matInput [type]="hide.confirm ? 'password' : 'text'" formControlName="confirmPassword" autocomplete="new-password">
          <button mat-icon-button matSuffix type="button" (click)="hide.confirm=!hide.confirm">
            <mat-icon>{{hide.confirm ? 'visibility_off' : 'visibility'}}</mat-icon>
          </button>
          <mat-error *ngIf="form.hasError('mismatch') && form.get('confirmPassword')?.touched">Passwords do not match</mat-error>
        </mat-form-field>
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="dialogRef.close()">Cancel</button>
      <button mat-raised-button color="primary" (click)="save()" [disabled]="saving">
        <mat-spinner diameter="18" *ngIf="saving" style="display:inline-block;margin-right:6px"></mat-spinner>
        Change Password
      </button>
    </mat-dialog-actions>
  `,
})
export class ChangePasswordDialogComponent {
  form: FormGroup;
  saving = false;
  hide = { current: true, new: true, confirm: true };

  constructor(
    public dialogRef: MatDialogRef<ChangePasswordDialogComponent>,
    private fb: FormBuilder,
    private authService: AuthService,
    private snackBar: MatSnackBar,
  ) {
    this.form = this.fb.group({
      currentPassword: ['', Validators.required],
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', Validators.required],
    }, { validators: passwordsMatch });
  }

  save(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    this.saving = true;
    const { currentPassword, newPassword } = this.form.value;
    this.authService.changePassword(currentPassword, newPassword).subscribe({
      next: () => {
        this.snackBar.open('Password changed successfully', '', { duration: 3000 });
        this.dialogRef.close(true);
      },
      error: () => { this.saving = false; },
    });
  }
}
